import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PiTrophyLight, PiCaretRightLight } from "react-icons/pi";
import client from "../../api/client";

const MEDALS = ["🥇", "🥈", "🥉"];

function Avatar({ name, img }) {
  const initials = (name || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
  if (img) return <img src={img} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />;
  return <div className="w-9 h-9 rounded-full bg-[#294a93] text-white text-xs flex items-center justify-center font-bold flex-shrink-0">{initials}</div>;
}

function PointHistoryModal({ student, endpoint, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get(`${endpoint}/${student.id}/point-history`)
      .then(res => setHistory(res.data.transactions || []))
      .catch(() => toast.error("Failed to load point history."))
      .finally(() => setLoading(false));
  }, [student.id, endpoint]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: "80vh" }}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900">Point History</h2>
            <p className="text-xs text-gray-400">{student.firstname} {student.lastname} · {student.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">No point transactions yet</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  <th className="text-left pb-3">Reason</th>
                  <th className="text-left pb-3">Type</th>
                  <th className="text-right pb-3">Points</th>
                  <th className="text-right pb-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {history.map(t => (
                  <tr key={t.id}>
                    <td className="py-2.5 text-gray-700 max-w-[160px] truncate">{t.reason || "—"}</td>
                    <td className="py-2.5 text-gray-400 text-xs capitalize">{t.reference_type || "—"}</td>
                    <td className={`py-2.5 text-right font-bold ${Number(t.amount) >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {Number(t.amount) >= 0 ? "+" : ""}{t.amount}
                    </td>
                    <td className="py-2.5 text-right text-xs text-gray-400">
                      {new Date(t.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-gray-100">
          <button onClick={onClose}
            className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminLeaderboardPage({ endpoint = "/admin/students" }) {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    const leaderboardUrl = endpoint === "/admin/students" ? "/admin/leaderboard" : "/instructor/leaderboard";
    client.get(leaderboardUrl)
      .then(res => setLeaders(res.data.leaderboard || []))
      .catch(() => toast.error("Failed to load leaderboard."))
      .finally(() => setLoading(false));
  }, [endpoint]);

  return (
    <div className="p-6 md:p-8 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <PiTrophyLight size={22} className="text-amber-500" /> Student Leaderboard
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">All students ranked by total points — click any row to see their history</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : leaders.length === 0 ? (
          <div className="p-16 text-center"><p className="text-4xl mb-3">🏆</p><p className="text-sm font-medium text-gray-600">No data yet</p></div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {leaders.map((u, i) => (
              <li key={u.id}
                onClick={() => setSelected(u)}
                className={`flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors ${i < 3 ? "bg-amber-50/40 hover:bg-amber-100/60" : "hover:bg-gray-50"}`}>
                <span className="text-lg w-8 text-center flex-shrink-0">
                  {i < 3 ? MEDALS[i] : <span className="text-sm text-gray-400 font-bold">#{i + 1}</span>}
                </span>
                <Avatar name={`${u.firstname} ${u.lastname}`} img={u.profile_img} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm">{u.firstname} {u.lastname}</p>
                  <p className="text-xs text-gray-400 truncate">{u.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-[#294a93]">{Number(u.total_points).toLocaleString()}</p>
                  <p className="text-xs text-gray-400">pts</p>
                </div>
                <PiCaretRightLight size={16} className="text-gray-300 flex-shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <PointHistoryModal student={selected} endpoint={endpoint} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
